import {useEffect, useState} from 'react';
import {useAtom} from 'jotai';
import {toast} from 'sonner';
import {notificationSettingsOpenAtom} from '@lib/atoms';
import {useAuth} from '@api/auth';
import {useActiveWorkspace, useWorkspaceModules} from '@api/workspaces';
import {
  useChannels,
  useDisconnectChannel,
  useNotificationSettings,
  useUpdateSettings,
  type ChannelName,
  type NotificationSections,
} from '@api/notifications';
import {
  NOTIFICATION_CHANNELS,
  channelMeta,
} from '@ui/local/notification-channels';
import {Button} from '@ui/shadcn/button';
import {Switch} from '@ui/shadcn/switch';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '@ui/shadcn/accordion';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '@ui/shadcn/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@ui/shadcn/alert-dialog';
import {ConnectFlow} from '@ui/local/ConnectFlow';
import {ChannelRow} from './ChannelRow';
import {SectionRow} from './SectionRow';
import {SimpleSectionRow} from './SimpleSectionRow';
import {
  settingsSections,
  settingsChanges,
  type NotificationSettingsDraft,
} from '../model/settings';

/** Настройки всех персональных уведомлений; подключение и подписка независимы. */
export const NotificationSettingsModal = () => {
  const [open, setOpen] = useAtom(notificationSettingsOpenAtom);
  const {currentUser} = useAuth();
  const {activeId, active} = useActiveWorkspace();
  // Недельные отчёты — часть модуля «Аналитические инсайты»: без него их не
  // рассылают, и настраивать нечего. Сохранённое значение не трогаем.
  const {analyticsInsights: insightsEnabled} = useWorkspaceModules();
  const settingsQuery = useNotificationSettings(open);
  const {data: channels = []} = useChannels(open);
  const updateSettings = useUpdateSettings();
  const disconnect = useDisconnectChannel();
  const [draft, setDraft] = useState<NotificationSections>(() =>
    settingsSections(),
  );
  const [subscriptions, setSubscriptions] = useState<string[]>([]);
  const [baseline, setBaseline] = useState<NotificationSettingsDraft | null>(
    null,
  );
  const [confirmCancel, setConfirmCancel] = useState(false);
  const [disconnectName, setDisconnectName] = useState<ChannelName | null>(
    null,
  );
  const [connectName, setConnectName] = useState<ChannelName | null>(null);
  const connected = {
    telegram: channels.some(
      (c) => c.channel === 'telegram' && c.connectionState === 'connected',
    ),
    max: channels.some(
      (c) => c.channel === 'max' && c.connectionState === 'connected',
    ),
  };

  useEffect(() => {
    if (!open || !settingsQuery.data) return;
    const sections = settingsSections(settingsQuery.data);
    const reportWorkspaceSids = settingsQuery.data.reportWorkspaceSids ?? [];
    setDraft(sections);
    setSubscriptions(reportWorkspaceSids);
    setBaseline({sections, reportWorkspaceSids});
  }, [open, settingsQuery.data]);

  const dirty =
    baseline !== null &&
    JSON.stringify(baseline) !==
      JSON.stringify({sections: draft, reportWorkspaceSids: subscriptions});
  const requestClose = () => {
    if (dirty) setConfirmCancel(true);
    else setOpen(false);
  };
  const save = () => {
    if (!baseline) return;
    updateSettings.mutate(
      settingsChanges(
        {sections: draft, reportWorkspaceSids: subscriptions},
        baseline,
      ),
      {
        onSuccess: () => {
          toast.success('Настройки сохранены');
          setOpen(false);
        },
        onError: () =>
          toast.error(
            'Не удалось сохранить настройки. Ваши изменения оставлены в форме.',
          ),
      },
    );
  };
  const attention = draft['attention-queue'];
  const disconnectChannel = () => {
    const connection = channels.find((c) => c.channel === disconnectName);
    setDisconnectName(null);
    if (connection)
      disconnect.mutate(connection.connectionSid, {
        onError: () => toast.error('Не удалось отключить канал'),
      });
  };

  return (
    <>
      <Dialog
        open={open}
        onOpenChange={(next) => {
          if (!next) requestClose();
        }}
      >
        <DialogContent className='min-w-0 sm:max-w-[600px] max-h-[calc(100dvh-2rem)] overflow-y-auto p-4 sm:p-6'>
          <DialogHeader>
            <DialogTitle>Настройки уведомлений</DialogTitle>
            <DialogDescription>
              Выберите типы уведомлений для колокольчика и мессенджеров.
              Telegram и MAX отдельно управляют внешней доставкой.
            </DialogDescription>
          </DialogHeader>
          <div className='min-w-0 space-y-4'>
            <section className='min-w-0 rounded-xl border border-[var(--color-border)] px-4 py-2'>
              <h3 className='text-sm font-semibold'>Каналы доставки</h3>
              {NOTIFICATION_CHANNELS.map((channel) => (
                <ChannelRow
                  key={channel.name}
                  channel={channel}
                  connected={connected[channel.name]}
                  onConnect={() => setConnectName(channel.name)}
                  onDisconnect={() => setDisconnectName(channel.name)}
                />
              ))}
            </section>
            {settingsQuery.isPending ? (
              <p role='status'>Загрузка настроек…</p>
            ) : settingsQuery.isError ? (
              <div role='alert' className='space-y-2'>
                <p>Не удалось загрузить настройки.</p>
                <Button
                  variant='outline'
                  onClick={() => void settingsQuery.refetch()}
                >
                  Повторить
                </Button>
              </div>
            ) : (
              <section className='min-w-0 rounded-xl border border-[var(--color-border)] px-4 divide-y divide-[var(--color-border)]'>
                {attention && (
                  <div className='min-w-0 py-4'>
                    <div className='flex items-center justify-between gap-3 mb-2'>
                      <h3 className='text-sm font-semibold'>
                        Требуют внимания
                      </h3>
                      <Switch
                        aria-label='Уведомления: требуют внимания'
                        className='shrink-0'
                        checked={attention.enabled}
                        onCheckedChange={(enabled) =>
                          setDraft({
                            ...draft,
                            'attention-queue': {...attention, enabled},
                          })
                        }
                      />
                    </div>
                    <p className='text-xs text-[var(--color-muted-foreground)] mb-2'>
                      Звонки, требующие быстрого реагирования.
                    </p>
                    {(['p1', 'p2'] as const).map((priority) => (
                      <SectionRow
                        key={priority}
                        label={
                          priority === 'p1'
                            ? 'Приоритет 1 (горячий лид)'
                            : 'Приоритет 2 (тёплый лид)'
                        }
                        settings={attention[priority]}
                        availableChannels={connected}
                        disabled={!attention.enabled}
                        onChange={(value) =>
                          setDraft({
                            ...draft,
                            'attention-queue': {
                              ...attention,
                              [priority]: {
                                telegram: value.telegram,
                                max: value.max,
                              },
                            },
                          })
                        }
                      />
                    ))}
                  </div>
                )}
                {(insightsEnabled
                  ? (['analytics-reports', 'tasks'] as const)
                  : (['tasks'] as const)
                ).map((name) => (
                  <SimpleSectionRow
                    key={name}
                    label={
                      name === 'tasks' ? 'Задачи' : 'Аналитические инсайты'
                    }
                    description={
                      name === 'tasks'
                        ? 'Назначения и изменения статуса'
                        : 'Еженедельный отчёт: негативная и позитивная динамика'
                    }
                    value={draft[name]}
                    connected={connected}
                    onChange={(value) => setDraft({...draft, [name]: value})}
                  />
                ))}
                {currentUser?.isOperator && activeId && insightsEnabled && (
                  <Accordion type='single' collapsible>
                    <AccordionItem value='operator-subscriptions'>
                      <AccordionTrigger className='text-xs text-[var(--color-muted-foreground)]'>
                        Дополнительно для оператора
                      </AccordionTrigger>
                      <AccordionContent>
                        <div className='flex items-center justify-between gap-3'>
                          <span className='min-w-0 break-words text-sm'>
                            Отчёты воркспейса «
                            {active?.name ?? 'текущий воркспейс'}»
                          </span>
                          <Switch
                            aria-label='Подписка на отчёты текущего воркспейса'
                            className='shrink-0'
                            checked={subscriptions.includes(activeId)}
                            disabled={
                              !subscriptions.includes(activeId) &&
                              subscriptions.length >= 100
                            }
                            onCheckedChange={(checked) =>
                              setSubscriptions(
                                checked
                                  ? [...subscriptions, activeId]
                                  : subscriptions.filter(
                                      (sid) => sid !== activeId,
                                    ),
                              )
                            }
                          />
                        </div>
                        <p className='mt-2 text-xs text-[var(--color-muted-foreground)]'>
                          Отдельная подписка оператора. Не изменяет права
                          доступа.
                        </p>
                      </AccordionContent>
                    </AccordionItem>
                  </Accordion>
                )}
              </section>
            )}
          </div>
          <DialogFooter>
            <Button variant='outline' onClick={requestClose}>
              Закрыть
            </Button>
            <Button
              disabled={
                !dirty || updateSettings.isPending || settingsQuery.isError
              }
              onClick={save}
            >
              {updateSettings.isPending ? 'Сохранение…' : 'Сохранить'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      <AlertDialog open={confirmCancel} onOpenChange={setConfirmCancel}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Закрыть без сохранения?</AlertDialogTitle>
            <AlertDialogDescription>
              Несохранённые изменения будут потеряны.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Продолжить редактирование</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => {
                setConfirmCancel(false);
                setOpen(false);
              }}
            >
              Закрыть
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
      <AlertDialog
        open={disconnectName !== null}
        onOpenChange={(next) => {
          if (!next) setDisconnectName(null);
        }}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              Отключить {disconnectName && channelMeta(disconnectName).label}?
            </AlertDialogTitle>
            <AlertDialogDescription>
              Доставка в этот канал прекратится. Настройки разделов сохранятся.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Отмена</AlertDialogCancel>
            <AlertDialogAction onClick={disconnectChannel}>
              Отключить
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
      <ConnectFlow
        open={connectName !== null}
        onClose={() => setConnectName(null)}
        connection={connected}
        startChannel={connectName ?? undefined}
      />
    </>
  );
};
